import React, { useState } from 'react';
import './App.css';

const MycoCTA = ({ message }) => (
  <div className="myco-cta">
    <p className="myco-cta-text">{message}</p>
    <a
      href="https://myconet.correa-oscar11.workers.dev/"
      target="_blank"
      rel="noopener noreferrer"
      className="myco-cta-link"
    >
      Enter MycoNet 🍄 →
    </a>
  </div>
);

function App() {
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      text: "Welcome to RTRN! 🌱 I'm your guide to the Regenerative Tribe Regenerative Neighborhood.\n\nAsk me anything about the community — cost, membership, location, amenities, and more!", 
      isBot: true 
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = { id: Date.now(), text: input, isBot: false };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: input })
      });
      const data = await response.json();
      
      const botMessage = { id: Date.now() + 1, text: data.response, isBot: true };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = { id: Date.now() + 1, text: "Sorry, I'm having trouble connecting. Please try again.", isBot: true };
      setMessages(prev => [...prev, errorMessage]);
    }

    setLoading(false);
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <a href="https://regentribe.co" target="_blank" rel="noopener noreferrer" className="logo">
            <span className="logo-icon">🌱</span>
            <span className="logo-text">RTRN</span>
          </a>
          <nav className="nav">
            <a href="https://regentribe.co/regenhoodzero" target="_blank" rel="noopener noreferrer">Learn More</a>
            <a href="mailto:hello@regentribe.org">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <img src="/hero.jpg" alt="RTRN Regenerative Neighborhood — solar-canopied homes, food forest, and central water temple" className="hero-banner" />
        <div className="hero-overlay">
          <h1>Regen Tribe — Regenerative Neighborhood</h1>
          <p>A 150-home community on 88 hectares — good for you, the collective, and the planet.</p>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="summary">
        <div className="summary-card">
          <h2>What is RTRN?</h2>
          <p>A <strong>150-home regenerative neighborhood</strong> on 88 hectares where residents co-own the property and share in the revenue. Built on 4-layer governance with AI-assisted decision making.</p>
          <div className="summary-highlights">
            <div className="highlight">
              <span className="highlight-num">$89K</span>
              <span className="highlight-label">Co-op Share</span>
            </div>
            <div className="highlight">
              <span className="highlight-num">88ha</span>
              <span className="highlight-label">Land Size</span>
            </div>
            <div className="highlight">
              <span className="highlight-num">14</span>
              <span className="highlight-label">Neighborhoods</span>
            </div>
            <div className="highlight">
              <span className="highlight-num">5 yrs</span>
              <span className="highlight-label">Build Timeline</span>
            </div>
          </div>
        </div>
      </section>

      <MycoCTA message="A 150-home co-op sounds ambitious. It is. See how MycoNet keeps 300 humans (mostly) in harmony." />

      {/* Financials Section */}
      <section className="financials">
        <h2>How It Works Financially</h2>
        <div className="finance-accordion">
          <details className="finance-card">
            <summary>
              <span className="finance-num">1</span>
              <span className="finance-label">One-Time Investment</span>
              <span className="finance-price">$89K</span>
            </summary>
            <p>Co-op share — covers your land, roads, water, common buildings, solar farm, retreat center, and food forest. Everyone pays the same, everyone gets equal access.</p>
          </details>
          <details className="finance-card">
            <summary>
              <span className="finance-num">2</span>
              <span className="finance-label">Your Home</span>
              <span className="finance-price">$115K–$540K</span>
            </summary>
            <p>Build your own home on your lot. Studios start at $115K, 4-bedrooms at $540K. You own it outright — no mortgage required.</p>
          </details>
          <details className="finance-card">
            <summary>
              <span className="finance-num">3</span>
              <span className="finance-label">Monthly Costs</span>
              <span className="finance-price">$250–$500</span>
            </summary>
            <p>Lot fee covers roads, water, energy share, and maintenance of all common spaces. Plus save ~$430/month on food & energy vs. traditional living.</p>
          </details>
          <details className="finance-card">
            <summary>
              <span className="finance-num">4</span>
              <span className="finance-label">You Earn</span>
              <span className="finance-price">$225–$560<span className="per-mo">/mo</span></span>
            </summary>
            <p>Revenue from the community's businesses — retreat center, spa, guest housing, farm-to-table dining, memberships, farm, plus art & vocational programs — is distributed to all members after operating costs. At full capacity that's $225–$560/month back to you, and it climbs as the businesses scale — with hardwood timber adding a significant long-term boost (Year 10–30+).</p>
          </details>
          <details className="finance-card">
            <summary>
              <span className="finance-num">5</span>
              <span className="finance-label">Vocational Roles</span>
              <span className="finance-price">$40K–$70K</span>
            </summary>
            <p>RTRN Vocational Institute trains residents for paid roles. Professional staff earn salaries while learning regenerative skills. Roles include: Farm Manager ($48K–$60K), Energy Systems Lead ($55K–$70K), Kitchen Director ($45K–$55K), Community Director ($50K–$65K), Wellness Lead ($45K–$55K). Housing + meals included. 89 total vocational positions available.</p>
          </details>
        </div>
        <p className="finance-note">Total to join as a Pioneer: ~<strong>$209K</strong> (co-op share + studio home + solar). No hidden fees.</p>
      </section>

      <MycoCTA message="Great returns are nice. Great neighbors are priceless. Meet the people your dividends come with — on MycoNet." />

      {/* Neighborhoods Section */}
      <section className="neighborhoods">
        <img src="/mandala.jpg" alt="RTRN Neighborhood Layout" className="mandala-image" />
        <h2>The 14 Neighborhoods</h2>
        <p>Each color-coded module represents an area of human flourishing — together forming a complete ecosystem where every need is served.</p>
        <div className="neighborhood-grid">
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#1a1a1a'}}></span>
            <strong>🖤 BLACK</strong> — Micro-Community & Guest Housing
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#8B4513'}}></span>
            <strong>🤎 BROWN</strong> — Food & Gastronomy
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#e53935'}}></span>
            <strong>❤️ RED</strong> — Creation & Creative Forces
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#ff9800'}}></span>
            <strong>🧡 ORANGE</strong> — Imagination & Makers Spaces
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#fbbc05'}}></span>
            <strong>💛 YELLOW</strong> — Fitness & Movement
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#34a853'}}></span>
            <strong>💚 GREEN</strong> — Longevity & Wellness
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#4285f4'}}></span>
            <strong>🩵 BLUE</strong> — Entrepreneurship & Innovation
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#3f51b5'}}></span>
            <strong>💙 INDIGO</strong> — Content Creation & Media
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#9c27b0'}}></span>
            <strong>💜 VIOLET</strong> — Cosmic, Spiritual & Metaphysical
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#e91e63'}}></span>
            <strong>🩷 PINK</strong> — Education & Growth
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#f5f5f5'}}></span>
            <strong>🤍 WHITE</strong> — Water Temple (Cleansing)
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#9e9e9e'}}></span>
            <strong>🩶 SILVER</strong> — Technology (Robotics, AI, IoT)
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#ffd700'}}></span>
            <strong>💛 GOLD</strong> — Cultural Heritage
          </div>
          <div className="neighborhood-row">
            <span className="dot" style={{background: '#795548'}}></span>
            <strong>🪵 WOOD</strong> — Natural Building & Craftsmanship
          </div>
        </div>
        <img src="/pink-neighborhood.jpg" alt="Pink Neighborhood - Education & Growth" className="neighborhood-feature-img" />
      </section>

      <MycoCTA message="Fourteen neighborhoods, zero group-chat chaos. That's MycoNet doing its quiet magic." />

      {/* Businesses Section */}
      <section className="businesses">
        <h2>The Businesses We're Building</h2>
        <p className="businesses-intro">
          RTRN runs on a <strong>cooperative model</strong>: every resident co-owns the land and shares in the revenue from a portfolio of community-operated businesses. Estimated annual revenue at full operation is <strong>~$1.8M–$2.85M</strong> — plus a long-horizon hardwood-timber stream that matures later (see below). Figures are projections; the co-op's conservative near-term community revenue is $1.5M–$2.2M/yr.
        </p>

        <h3 className="biz-subhead">Flagship Enterprises</h3>
        <p className="biz-note-line">💰 = estimated annual revenue at full operation</p>
        <div className="biz-grid">
          {[
            { icon: '🧘', name: '50+ Guest Retreat Center', est: '$635K–$912K/yr', desc: 'Fully independent facilities hosting wellness, corporate, and creative retreats — the largest single revenue stream.' },
            { icon: '🎟️', name: 'Community Amenities Membership', est: '$540K–$660K/yr', desc: 'Lot fees plus tiered memberships and access to gyms, co-working, labs, wellness centers, and shared infrastructure.' },
            { icon: '💆', name: 'Experiential Spa & Water Temple', est: '$150K–$300K/yr', desc: 'Sauna, steam, cenote pools, hamam, and six-senses ceremony experiences with retreat and membership packages.' },
            { icon: '🏨', name: 'Guest Housing & Short-Term Rentals', est: '$120K–$250K/yr', desc: 'Chic hotel-style guest housing and micro-community stays for visitors and prospective members.' },
            { icon: '🍽️', name: 'Farm-to-Table Restaurant & Café', est: '$100K–$200K/yr', desc: 'Communal dining, cafés, and a mushroom/fermentation lab powered by our own permaculture harvest.' },
            { icon: '🎓', name: 'Vocational Institute & Work Co-op', est: '$80K–$180K/yr', desc: 'Paid training and job placement across 89 vocational roles — from farm management to energy systems and wellness.' },
            { icon: '🌱', name: 'Food Forest & Farm', est: '$80K–$150K/yr', desc: '25-hectare permaculture food forest — produce, eggs, honey, and milk. Steady, near-term revenue from year one of harvests.' },
            { icon: '🎨', name: 'Art Programs & Installations', est: '$60K–$120K/yr', desc: 'Classes, open studios, commissions, and art installations across the property — from painting to performing arts.' },
            { icon: '🐐', name: 'Animal Sanctuary & Therapy Center', est: '$30K–$80K/yr', desc: 'Animal sanctuary, therapy programs, and an adoption center integrated into daily community life.' },
            { icon: '🌲', name: 'Hardwood Timber (Agroforestry)', est: '$255K–$525K/yr at maturity', tag: '⏳ Long-term: revenue Year 10–30', desc: '~8,400 hardwood trees (teak, cedar, mahogany) planted as windbreaks. Timber is a long-horizon asset: first fence-post harvest Year 8–10, teak/cedar Year 25–30. Carbon credits ($15K–$40K/yr) provide interim income while trees mature.' }
          ].map((b, i) => (
            <div key={i} className={`biz-card${b.tag ? ' biz-card-longterm' : ''}`}>
              <span className="biz-icon">{b.icon}</span>
              <div className="biz-body">
                <h4>{b.name}</h4>
                <p>{b.desc}</p>
                <div className="biz-meta">
                  <span className="biz-est">💰 {b.est}</span>
                  {b.tag && <span className="biz-tag">{b.tag}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="biz-timber-note">
          <strong>Why hardwood timber is shown separately:</strong> unlike the food forest — which earns from year one — timber trees take <strong>10–30 years</strong> to reach harvest. They protect crops and buildings as windbreaks immediately, sequester carbon (earning credits in the interim), and appreciate as a stored-value asset that pays out decades later. It's a legacy revenue stream for the community, deliberately excluded from the near-term totals above.
        </p>

        <h3 className="biz-subhead">Revenue by Neighborhood</h3>
        <p className="businesses-intro">Each of the 14 neighborhoods is also its own micro-economy with a dedicated revenue model:</p>
        <div className="biz-revenue">
          {[
            { color: '#1a1a1a', name: '🖤 Black — Micro-Community', rev: 'Short-term rentals, membership fees, event ticket sales' },
            { color: '#8B4513', name: '🤎 Brown — Food & Gastronomy', rev: 'Produce sales, dining memberships, restaurant & café revenue, artisanal goods' },
            { color: '#e53935', name: '❤️ Red — Creation', rev: 'Workshop fees, creative consultations, immersive experience tickets' },
            { color: '#ff9800', name: '🧡 Orange — Makers Spaces', rev: 'Studio rentals, course fees, handcrafted goods, commissions, gallery rentals' },
            { color: '#fbbc05', name: '💛 Yellow — Fitness & Movement', rev: 'Fitness memberships, personal training, class packs, workshops' },
            { color: '#34a853', name: '💚 Green — Longevity & Wellness', rev: 'Spa & wellness services, membership tiers, health consultations, product retail' },
            { color: '#4285f4', name: '🩵 Blue — Entrepreneurship', rev: 'Co-working memberships, business consulting, incubator & lab access fees' },
            { color: '#3f51b5', name: '💙 Indigo — Media', rev: 'Studio rentals, editing & production, content licensing, online course sales' },
            { color: '#9c27b0', name: '💜 Violet — Spiritual', rev: 'Course & workshop fees, retreat packages' },
            { color: '#e91e63', name: '🩷 Pink — Education', rev: 'AI-customized education programs, time-share / cost-share tuition' },
            { color: '#f5f5f5', name: '🤍 White — Water Temple', rev: 'Retreat packages, ceremony fees, spa services' },
            { color: '#9e9e9e', name: '🩶 Silver — Technology', rev: 'Tech service fees, lab access, consulting' },
            { color: '#ffd700', name: '💛 Gold — Cultural Heritage', rev: 'Tours, museum admissions, cultural events' },
            { color: '#795548', name: '🪵 Wood — Natural Building', rev: 'Workshop fees, treehouse hospitality, commissioned builds, lumber & festival sales' }
          ].map((n, i) => (
            <div key={i} className="biz-revenue-row">
              <span className="dot" style={{background: n.color}}></span>
              <div>
                <strong>{n.name}</strong>
                <span className="biz-revenue-model">{n.rev}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <MycoCTA message="Every one of these businesses runs on MycoNet. Pop the hood and see the engine." />

      {/* Chat Interface */}
      <main className="chat-container">
        <div className="chat-box">
          <div className="chat-messages">
            {messages.map((msg) => (
              <div key={msg.id} className={`message ${msg.isBot ? 'bot' : 'user'}`}>
                {msg.isBot && <span className="bot-avatar">🌱</span>}
                <div className="message-content">
                  {msg.text.split('\n').map((line, i) => {
                    if (line.startsWith('**') && line.endsWith('**')) {
                      return <p key={i}><strong>{line.replace(/\*\*/g, '')}</strong></p>;
                    }
                    if (line.startsWith('|')) {
                      return <pre key={i} className="table-line">{line}</pre>;
                    }
                    if (line.startsWith('•') || line.startsWith('🌱') || line.startsWith('🏠') || line.startsWith('🌿')) {
                      return <p key={i} className="bullet-line">{line}</p>;
                    }
                    return <p key={i}>{line}</p>;
                  })}
                </div>
                {!msg.isBot && <span className="user-avatar">👤</span>}
              </div>
            ))}
            {loading && (
              <div className="message bot">
                <span className="bot-avatar">🌱</span>
                <div className="message-content typing">Thinking...</div>
              </div>
            )}
          </div>

          <form className="chat-input" onSubmit={handleSubmit}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about RTRN..."
              disabled={loading}
            />
            <button type="submit" disabled={loading || !input.trim()}>
              {loading ? '...' : '→'}
            </button>
          </form>
        </div>
      </main>

      {/* Quick Topics */}
      <section className="quick-topics">
        <h3>Quick Topics</h3>
        <div className="topic-grid">
          <button onClick={() => setInput('How much does it cost to join?')}>💰 Cost</button>
          <button onClick={() => setInput('How do I become a member?')}>🙋 Join</button>
          <button onClick={() => setInput('Where is the community located?')}>📍 Location</button>
          <button onClick={() => setInput('What amenities are included?')}>🏡 Amenities</button>
          <button onClick={() => setInput('What businesses does the community run?')}>🏢 Businesses</button>
          <button onClick={() => setInput('What is the timeline?')}>📅 Timeline</button>
          <button onClick={() => setInput('How does governance work?')}>⚖️ Governance</button>
        </div>
      </section>

      {/* Financial Breakdown */}
      <section className="finance-breakdown">
        <h2>The Money: Where It Goes & How It Returns</h2>
        
        <div className="finance-cols">
          <div className="finance-col">
            <h3>Where the Money Goes</h3>
            <p>The $13.4M co-op infrastructure builds everything residents share:</p>
            <table className="finance-table">
              <tr><td>Land (88 hectares)</td><td>$1M</td></tr>
              <tr><td>Roads + site clearing</td><td>$300K</td></tr>
              <tr><td>Water systems</td><td>$350K</td></tr>
              <tr><td>21 Common buildings</td><td>$7.4M</td></tr>
              <tr><td>Retreat center (50 guests)</td><td>$1.35M</td></tr>
              <tr><td>Solar + battery farm</td><td>$600K</td></tr>
              <tr><td>Food forest + animals</td><td>$350K</td></tr>
              <tr><td>Working capital + contingency</td><td>$2.05M</td></tr>
            </table>
            <p className="sub">150 members × $89K co-op share = $13.4M — fully funds the shared infrastructure.</p>
          </div>

          <div className="finance-col">
            <h3>How Money Returns</h3>
            <p>The community's businesses earn revenue at full operation:</p>
            <table className="finance-table">
              <tr><td>Retreat center</td><td>$635–912K/yr</td></tr>
              <tr><td>Memberships + lot fees</td><td>$540–660K/yr</td></tr>
              <tr><td>Spa & Water Temple</td><td>$150–300K/yr</td></tr>
              <tr><td>Guest housing & rentals</td><td>$120–250K/yr</td></tr>
              <tr><td>Restaurant & café</td><td>$100–200K/yr</td></tr>
              <tr><td>Vocational institute</td><td>$80–180K/yr</td></tr>
              <tr><td>Food forest & farm</td><td>$80–150K/yr</td></tr>
              <tr><td>Art programs</td><td>$60–120K/yr</td></tr>
              <tr><td>Animal sanctuary</td><td>$30–80K/yr</td></tr>
              <tr><td>🌲 Hardwood timber <span className="lt">Yr 10–30+</span></td><td>$255–525K/yr</td></tr>
            </table>
            <p className="sub">Near-term total ~$1.8–2.85M/yr (plus solar + carbon credits ~$200–350K/yr). After operating costs ($775K) and investor returns ($280K), surplus is distributed to members. Hardwood timber is a long-term stream, excluded from near-term totals.</p>
          </div>
        </div>
      </section>

      <MycoCTA message="Now you know where the money goes. Come meet the people who make it grow — on MycoNet." />

      {/* FAQ Section */}
      <section className="faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {[
            { q: "Do I need farming experience to join?", a: "No! We teach everything. Many members join with zero farming knowledge — our vocational training covers permaculture, animal care, and all the skills you need." },
            { q: "What if I don't want to do animal care?", a: "No problem. You can focus on your vocational area (art, tech, wellness, etc.) while the professional team covers animal shifts. Or opt for our turnkey agricultural service for an extra fee." },
            { q: "Can I leave early if I change my mind?", a: "Yes. Resident tier is month-to-month. Pioneer and Founder members have exit clauses in their agreements. Your home can be sold to a qualified buyer who joins the co-op." },
            { q: "Is this a cult?", a: "No. We're a legitimate community with clear governance, no guru, and all decisions recorded on chain. We practice conscious communication and consent-based decision making." },
            { q: "What's the internet and work situation?", a: "High-speed WiFi throughout the community. Many members work remote jobs in tech, design, consulting, and entrepreneurship while living here full-time." },
            { q: "What do I need to bring?", a: "Personal items, clothing, laptop/phone, and willingness to contribute. We provide: furnished room, communal meals, animal care supplies, garden tools, and studio equipment." },
            { q: "How does the food system work?", a: "Our 25-hectare permaculture food forest produces 80%+ of our produce. We grow fruits, vegetables, nuts, and herbs. Animal sanctuary provides eggs, milk, and honey. Local farm partnerships cover grains and legumes." },
            { q: "What happens during the 13-week onboarding?", a: "You learn everything: site systems, governance (RCOS), your vocational role, animal care, permaculture principles, communal cooking, conscious communication, and community culture. Graduated by community vote." },
            { q: "Can I have a private home business?", a: "Yes! Many members run businesses from their homes or the co-working spaces. The community supports entrepreneurship through our BLUE neighborhood incubator, mentorship, and access to funding." },
            { q: "How does the revenue sharing work?", a: "After operating costs and investor returns, surplus is distributed to members. At full capacity (150 residents), each member receives approximately $225–$560/month in dividends — plus savings on energy and food costs." }
          ].map((item, i) => (
            <details key={i} className="faq-item">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta">
        <div className="cta-inner">
          <span className="cta-eyebrow">🍄 Powered by MycoNet</span>
          <h2>Like what you see? Come meet the community.</h2>
          <p>
            MycoNet is the AI-powered operating system behind RTRN — supporting the organization,
            operations, and growth of the community. Step inside to connect with other residents
            and experience how the neighborhood actually runs.
          </p>
          <a
            href="https://myconet.correa-oscar11.workers.dev/"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button"
          >
            Enter MycoNet →
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>© 2026 Regen Tribe. All rights reserved.</p>
        <p>🌐 regentribe.co/regenhoodzero | 📧 hello@regentribe.org</p>
      </footer>
    </div>
  );
}

export default App;